import { useQueryClient } from "@tanstack/react-query";
import html2canvas from "html2canvas";
import { supabase } from "index";
import jsPDF from "jspdf";
import { useCallback, useState } from "react";
import { api } from "utils/api";

export interface StatementGenerationOptions {
  element: HTMLElement;
  name: string;
  loanId: string;
}

async function generatePdf({ element }: { element: HTMLElement }): Promise<jsPDF> {
  const margin = 10;

  const loadImages = () => {
    const imgs = Array.from(element.querySelectorAll("img"));
    return Promise.all(
      imgs.map(img => {
        if (img.complete) return Promise.resolve();
        return new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject();
        });
      })
    );
  };

  await loadImages();

  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
  });

  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "px",
    format: "a4",
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentWidth = pageWidth - margin * 2;
  const contentHeight = pageHeight - margin * 2;

  const canvasWidth = canvas.width;
  const canvasHeight = canvas.height;

  const ratio = contentWidth / canvasWidth;
  const pageCanvasHeightPx = contentHeight / ratio;

  const totalPages = Math.ceil(canvasHeight / pageCanvasHeightPx);

  for (let page = 0; page < totalPages; page++) {
    const isLastPage = page === totalPages - 1;

    const sliceHeight = isLastPage ? canvasHeight - page * pageCanvasHeightPx : pageCanvasHeightPx;

    const pageCanvas = document.createElement("canvas");
    pageCanvas.width = canvasWidth;
    pageCanvas.height = sliceHeight;

    const ctx = pageCanvas.getContext("2d");
    if (!ctx) continue;

    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

    ctx.drawImage(
      canvas,
      0,
      page * pageCanvasHeightPx,
      canvasWidth,
      sliceHeight,
      0,
      0,
      canvasWidth,
      sliceHeight
    );

    const imgData = pageCanvas.toDataURL("image/jpeg", 0.85);

    if (page > 0) pdf.addPage();

    const scaledHeight = (sliceHeight * contentWidth) / canvasWidth;

    pdf.addImage(imgData, "PNG", margin, margin, contentWidth, scaledHeight, undefined, "FAST");
  }

  return pdf;
}

async function uploadPdf(pdf: jsPDF, name: string, loanId: string): Promise<string> {
  const fileId = name;
  const path = `loans/${loanId}/statements/${fileId}.pdf`;

  const { error } = await supabase.storage.from("statements").upload(path, pdf.output("blob"), {
    contentType: "application/pdf",
    upsert: true,
  });

  if (error) {
    throw error;
  }

  return path;
}

async function createStatement({
  name,
  loanId,
  path,
}: {
  name: string;
  loanId: string;
  path: string;
}): Promise<{ statementId: string }> {
  const response = await api.post(`/loans/${loanId}/statements`, {
    name,
    type: "PDF",
    path,
  });

  return response.data;
}

export function useStatement() {
  const [isSaving, setIsSaving] = useState(false);
  const queryClient = useQueryClient();

  const save = useCallback(
    async ({ element, name, loanId }: StatementGenerationOptions): Promise<jsPDF> => {
      setIsSaving(true);

      try {
        const pdf = await generatePdf({ element });
        const path = await uploadPdf(pdf, name, loanId);
        await createStatement({ name, loanId, path });

        queryClient.invalidateQueries({
          queryKey: ["statements", loanId],
        });

        return pdf;
      } catch (err) {
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [queryClient]
  );

  return {
    save,
    isSaving,
  };
}
